import { useCallback } from 'react';
import { useQuery, useMutation } from 'react-query';

import { queryClient, request, toastError, useCurrentUserId } from '@/utils';

import { UnionBadge } from '../types';
import { transBadge } from '../utils';
import { useIsOfficialBadge } from './official';

export const useBadgeDetail = (id: string) => {
  const currentAddress = useCurrentUserId();
  const isOfficialBadge = useIsOfficialBadge();
  const url = isOfficialBadge ? '/official_badge' : '/badge';
  const select = useCallback(({ badge }: { badge: UnionBadge }) => {
    return transBadge(badge);
  }, []);

  return useQuery(
    ['badge-detail', currentAddress, url, id],
    () => request.get<{ badge: UnionBadge }>(url, { id }),
    {
      select,
    },
  );
};

export const useBadgeHasMinted = (id: string) => {
  const currentAddress = useCurrentUserId();
  const isOfficialBadge = useIsOfficialBadge();
  const url = isOfficialBadge ? '/official_badge/user_has_minted' : '/badge/user_has_minted';

  return useQuery(
    ['badge-mint-info', currentAddress, url, id],
    () => request.get<{ has_minted: boolean; inner_id: number; mint_at: number }>(url, { id }),
    {
      enabled: !!currentAddress,
    },
  );
};

export const useBadgeCanMinted = ({
  id,
  enabled,
  isOfficial,
}: {
  id: string;
  enabled?: boolean;
  isOfficial: boolean;
}) => {
  const currentAddress = useCurrentUserId();
  const url = isOfficial ? '/official_badge/user_can_mint' : '/badge/user_can_mint';

  return useQuery(
    [url, currentAddress, id],
    () => request.get<{ can_mint: boolean }>(url, { id }),
    {
      enabled: !!currentAddress && enabled,
      select: res => res?.can_mint,
    },
  );
};

export const useBadgeCanClaim = (id: string, enabled?: boolean) => {
  const currentAddress = useCurrentUserId();
  const url = '/official_badge/code/user_can_claim';

  return useQuery(
    [url, currentAddress, id],
    () => request.get<{ can_claim: boolean }>(url, { id }),
    {
      enabled: !!currentAddress && enabled,
      select: res => res?.can_claim,
    },
  );
};

export const useBadgeMint = ({ id, isOfficial }: { id: string; isOfficial: boolean }) => {
  const currentAddress = useCurrentUserId();
  const url = isOfficial ? '/official_badge/mint' : '/badge/mint';
  return useMutation(
    (token?: string) => {
      return request.post<{ is_success: boolean; inner_id: number }>(url, { id, token });
    },
    {
      onSuccess({ inner_id, is_success }) {
        if (is_success) {
          queryClient.invalidateQueries(['badge-mint-list', currentAddress]);
          queryClient.invalidateQueries(['badge-detail', currentAddress]);
          queryClient.setQueriesData<
            { has_minted: boolean; inner_id: number; mint_at: number } | undefined
          >(['badge-mint-info', currentAddress], pre => {
            if (!pre) {
              return pre;
            }

            return {
              has_minted: true,
              mint_at: Date.now() / 1000,
              inner_id: inner_id,
            };
          });
        }
        // list 应该不用 setquery，因为不会同时在一个页面
      },
      onError(err: any) {
        toastError(err, 'failed to mint, please try later');
      },
    },
  );
};
